import React, { useContext, useState, useEffect, useMemo, useRef } from 'react'
import Axios from "axios";
import domain from "../../../util/domain";
import UserContext from "../../../context/UserContext";
import VisitsListView from './VisitsListView';
import Box from '@mui/joy/Box';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemContent from '@mui/joy/ListItemContent';
import Typography from '@mui/joy/Typography';
import ListDivider from "@mui/joy/ListDivider";
import { useNavigate } from "react-router-dom";
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import AddIcon from '@mui/icons-material/Add';
import { Button, Input, Sheet } from '@mui/joy';
import SearchIcon from '@mui/icons-material/Search';

function VisitDetails() {
    const history = useNavigate()
    const { user } = useContext(UserContext);
    //console.log("user: ", user);
    //console.log("VisitDetails.js");
    const [visitDetails, setVisitDetails] = useState([]);
    const [pageno, setPageno] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchPage, setSearchPage] = useState(0);
    const [searchHasMore, setSearchHasMore] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const searchTimeoutRef = useRef(null);

    useEffect(() => {
        if (!user) {
            setVisitDetails([]);
            setSearchResults([]);
        } else if (!isSearching) {
            getVisitDetails();
        }
    }, [user, isSearching]);

    useEffect(() => {
        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchQuery.trim().length >= 2) {
            setIsSearching(true);
            setSearchPage(0);
            setSearchResults([]); // Clear previous results when starting new search
            // Debounce search by 300ms
            searchTimeoutRef.current = setTimeout(() => {
                searchVisits(searchQuery, 0);
            }, 300);
        } else {
            setIsSearching(false);
            setSearchResults([]);
            setSearchPage(0);
            setSearchHasMore(false);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    async function getVisitDetails() {
        //console.log("Getting visit details");
        const visitResponse = await Axios.get(`${domain}/visits/`);
        setHasMore(visitResponse.data.length === 20);
        setVisitDetails(visitResponse.data);
    }

    async function searchVisits(query, page = 0) {
        try {
            const response = await Axios.get(`${domain}/visits/search`, {
                params: {
                    q: query,
                    limit: 20,
                    offset: page * 20
                }
            });
            const { results, hasMore } = response.data;
            setSearchHasMore(hasMore);
            if (page === 0) {
                setSearchResults(results);
            } else {
                setSearchResults(prev => [...prev, ...results]);
            }
        } catch (error) {
            console.error('Error searching visits:', error);
            setSearchResults([]);
            setSearchHasMore(false);
        }
    }
    function handleClick(event, visit) {
        if (event.defaultPrevented) return;
        event.preventDefault();
        history("/visits/details", { state: { visit } });
    }

    const addVisit = (event) => {
        if (event.defaultPrevented) return;
        event.preventDefault();
        history("/visits/add");
    };
    function renderVisitDetails() {
        //console.log("visitDetails: ", displayedVisits);
        return displayedVisits.map((visit, index) => (
            <React.Fragment key={visit.id || index}>
                <VisitsListView visit={visit} action={(event) => handleClick(event, visit)} />
                {index !== displayedVisits.length - 1 && <ListDivider inset="gutter" />}
            </React.Fragment>
        ));
    }
    const loadmore = async () => {
        //console.log("loadmore");
        const nextPage = pageno + 1;
        setPageno(nextPage);
        const visitResponse = await Axios.get(`${domain}/visits?pageno=${nextPage}`);
        setHasMore(visitResponse.data.length === 20);
        setVisitDetails(oldvisits => [...oldvisits, ...visitResponse.data]);
    }

    const loadMoreSearch = async () => {
        const nextPage = searchPage + 1;
        setSearchPage(nextPage);
        await searchVisits(searchQuery, nextPage);
    }

    const displayedVisits = useMemo(() => {
        return isSearching ? searchResults : visitDetails;
    }, [isSearching, searchResults, visitDetails]);

    const displayedHasMore = useMemo(() => {
        return isSearching ? searchHasMore : hasMore;
    }, [isSearching, searchHasMore, hasMore]);

    const canLoadMore = useMemo(() => {
        if (isSearching) {
            return displayedHasMore && searchResults.length % 20 === 0;
        } else {
            return displayedHasMore && visitDetails.length % 20 === 0;
        }
    }, [isSearching, displayedHasMore, searchResults.length, visitDetails.length]);
    return (
        <List>
            <ListItem>
                <ListItemContent>
                    <Typography level="title-lg">Visits</Typography>
                </ListItemContent>
                <ListItemDecorator sx={{ width: "44px" }} onClick={addVisit}>
                    <AddIcon sx={{ width: "44px" }} />
                </ListItemDecorator>
            </ListItem>
            <ListItem>
                <ListItemContent>
                    <Sheet variant="outlined" sx={{ p: 1, borderRadius: 'sm' }}>
                        <Input
                            placeholder="Search by college name, code, address or website"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            startDecorator={<SearchIcon />}
                            sx={{ width: '100%' }}
                        />
                    </Sheet>
                </ListItemContent>
            </ListItem>
            {
                (displayedVisits.length > 0) ?
                    <Box sx={{}}>
                        <List
                            variant="outlined"
                            aria-labelledby="ellipsis-list-demo"
                            sx={{ '--ListItemDecorator-size': '56px', borderRadius: "sm" }}
                        >
                            {renderVisitDetails()}
                            {canLoadMore ? (
                                <ListItem>
                                    <ListItemContent>
                                        <Typography level="title-lg">
                                            <Button
                                                variant="outlined"
                                                color="neutral"
                                                onClick={isSearching ? loadMoreSearch : loadmore}
                                            >
                                                View More
                                            </Button>
                                        </Typography>
                                    </ListItemContent>
                                </ListItem>
                            ) : ""}
                        </List>
                    </Box>
                    : (
                        <ListItem>
                            <ListItemContent>
                                <Typography level="body-md">
                                    {isSearching ? "No visits found" : "No visits"}
                                </Typography>
                            </ListItemContent>
                        </ListItem>
                    )
            }
        </List>
    )
}

export default VisitDetails;
